import React, { ReactElement, ReactNode, useState } from "react";
import { IconType } from "react-icons";
import { AiOutlineFileText } from "react-icons/ai";
import {
    FaFilePowerpoint, FaRegFileAudio, FaRegFileCode, FaRegFileImage,
} from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import { GoFile, GoFileCode, GoFileZip } from "react-icons/go";
import {
    GrDocumentPdf, GrDocumentWord, GrDocumentZip, GrFormClose,
} from "react-icons/gr";
import { RiFileExcel2Fill } from "react-icons/ri";
import classNames from "classnames";
import { RiDownloadCloud2Line, RiFileImageLine, RiMoreFill } from "@remixicon/react";
import Confirm from "../confirm/Confirm";
import { LoadingIcon } from "../loading/Loading";
import Styles from "./Attachment.module.scss";
import { useTheme } from "../../contexts/Theme";
import Button from "../theme/button/Button";
import Link from "../theme/button/Link";

const IMAGE_MIME_TYPES = new Set([
    "image/tiff",
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/apng",
    "image/bmp",
    "image/x-icon",
    "image/svg+xml",
    "image/webp",
]);

const VIDEO_MIME_TYPE = new Set([
    "application/vnd.apple.mpegurl",
    "application/x-mpegurl",
    "video/3gpp",
    "video/mp4",
    "video/mpeg",
    "video/ogg",
    "video/quicktime",
    "video/webm",
    "video/x-m4v",
    "video/ms-asf",
    "video/x-ms-wmv",
    "video/x-msvideo",
]);

const MIME_TO_IMAGE: Record<string, IconType> = {
    "application/pdf": GrDocumentPdf,
    "application/zip": GrDocumentZip,
    "application/msword": GrDocumentWord,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": GrDocumentWord,
    "application/gzip": GoFileZip,
    "application/x-tar": GoFileZip,
    "text/html": GoFile,
    "text/plain": AiOutlineFileText,
    "application/json": GoFileCode,
    "application/vnd.ms-powerpoint": FaFilePowerpoint,
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": FaFilePowerpoint,
    "application/vnd.ms-excel": RiFileExcel2Fill,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": RiFileExcel2Fill,
    "text/csv": RiFileExcel2Fill,
    "application/xml": FaRegFileCode,
    "text/xml": FaRegFileCode,

};

export type SIZE ="xs" | "sm" | "md" | "lg";

const SIZE_TO_TEXT_LENGTH: Record<SIZE | "default", number> = {
    xs: 13,
    sm: 20,
    md: 30,
    lg: 40,
    default: 40,
};

const AUDIO_ICON = FaRegFileAudio;

const DEFAULT_ICON = FaRegFileImage;

export interface AttachmentProps {
    mimeType?: string;
    title?: string;
    isUploading?: boolean;
    url: string;
    size?: SIZE;
    onClose?: () => void
    className?: string;
    controls?: ReactNode;
    controlsClassName?: string;
    variant?: "preview" | "uploaded";
}

function removeMiddleString(content: string | undefined, length: number): string | undefined {
    return (!content || content.length <= length)
        ? content
        : (`${content.substring(0, length / 2)}...${content.substring(content.length - (length / 2))}`);
}

const ATTACHMENT_CONTEXT = "attachments";
export default function Attachment({
    mimeType, title, url, size, onClose, className, isUploading, variant, controls, controlsClassName,
}: AttachmentProps): ReactElement {
    const [isFullScreen, setFullScreen] = useState(false);
    const theme = useTheme();

    if (isUploading) {
        return (
            <div className={className}>
                <LoadingIcon className={Styles["loading-icon-sm"]} />
            </div>
        );
    }

    const closeButton = onClose ? (
        <Button
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onClose();
            }}
            variant="none"
            size="small"
            className={Styles["close-button"]}
            aria-label="Remove attachment"
            isClose={theme.isV3}
            prefixIcon={!theme.isV3 ? <GrFormClose /> : undefined}
        />
    ) : undefined;

    const titlePanel = (size !== "sm" || variant === "preview") ? (
        <div className={theme.isV3 ? Styles["download-title"] : "px-2 d-flex justify-content-between"}>
            {title && (
                <div className={theme.isV3 ? Styles["download-title-text"] : "text-light"}>
                    {title}
                </div>
            )}

            {theme.isV3 ? (
                <div className={classNames(Styles["btn-download"], controlsClassName)}>
                    {controls}
                    <Link
                        size="small"
                        variant="secondary"
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        title="Download the attachment."
                        aria-label="Download the attachment."
                        prefixIcon={<RiDownloadCloud2Line />}
                    />
                </div>

            ) : (
                <a
                    className="text-decoration-none text-center text-center d-block"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    title={title}
                    aria-label="Download the attachment."
                >
                    {theme.isV3 ? "Download" : <FiDownload />}
                </a>
            )}

        </div>
    ) : undefined;

    const isIconOnly = size === "xs";

    const isVideo = VIDEO_MIME_TYPE.has(mimeType as string);
    const shouldShowModal = IMAGE_MIME_TYPES.has(mimeType as string) || (isIconOnly && isVideo);
    if (shouldShowModal) {
        const makeFullscreen = (event: React.MouseEvent) => {
            event.preventDefault();
            setFullScreen(true);
        };
        return (
            <>
                <Confirm
                    size="xl"
                    shouldShow={isFullScreen}
                    closeText="Close"
                    onHide={() => setFullScreen(false)}
                    dialogClassName="full-size-container"
                    keyboardContext={ATTACHMENT_CONTEXT}
                >
                    <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                        {isVideo ? (
                            <video
                                controls
                                width="100%"
                                className={size === "sm" ? "" : "flex-grow-1 flex-shrink-1"}
                            >
                                <source
                                    src={url}
                                    type="video/mp4"
                                />
                                <track kind="captions" />
                                Sorry, your browser doesn&apos;t support embedded videos.
                            </video>
                        ) : (

                            <img src={url} alt={title as string} className={`${Styles["dont-oversize"]} m-4 d-block`} />
                        )}
                    </div>
                </Confirm>

                {isIconOnly ? (
                    <button
                        type="button"
                        className={classNames(
                            Styles["icon-container"],
                            Styles[`attachment-${variant}`],
                            Styles[`attachment-${size}`],
                        )}
                        onClick={makeFullscreen}
                        title={title}
                        aria-label="Open attachment in fullscreen"
                    >
                        <RiFileImageLine className="fs-1 p-2 icon" />
                        <span className={Styles["attachment-name"]}>
                            {removeMiddleString(title, SIZE_TO_TEXT_LENGTH[size ?? "default"]) || "[Attachment]"}
                        </span>
                    </button>
                ) : (
                    <div
                        className={theme.isV3
                            ? classNames(Styles["image-container"], Styles[`attachment-${variant}`], className)
                            : classNames(
                                "border rounded",
                                size === "sm" ? "d-flex align-items-center m-0 p-0" : " m-0 p-0 col-6",
                                className,
                                // eslint-disable-next-line react/jsx-indent
                            )}
                    >

                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-decoration-none"
                            onClick={makeFullscreen}
                            title={title}
                            aria-label="Open attachment in fullscreen"
                        >
                            <img
                                src={url}
                                alt={title as string}
                                className={theme.isV3 ? "" : classNames(
                                    size === "sm" ? Styles[`size-${size}`] : "card-img-top",
                                )}
                            />
                        </a>
                        {closeButton}
                        {titlePanel}

                    </div>
                )}

            </>
        );
    }
    if (isVideo) {
        return (
            <div
                className={theme.isV3
                    ? classNames(Styles["video-container"], Styles[`attachment-${variant}`], className)
                    : classNames(
                        size === "sm" ? Styles[`size-${size}`] : "flex-grow-0 col-11 card",
                        className,
                        // eslint-disable-next-line react/jsx-indent
                    )}
            >
                <video
                    controls
                    width="100%"
                    className={size === "sm" ? "" : "flex-grow-1 flex-shrink-1"}
                >
                    <source
                        src={url}
                        type="video/mp4"
                    />
                    <track kind="captions" />
                    Sorry, your browser doesn&apos;t support embedded videos.
                </video>
                {closeButton}
                {titlePanel}

            </div>
        );
    }
    const Icon = (mimeType?.startsWith("audio/") ? AUDIO_ICON : MIME_TO_IMAGE[mimeType ?? ""]) ?? DEFAULT_ICON;
    return (
        <a
            className={theme.isV3
                ? classNames(Styles["icon-container"], Styles[`attachment-${variant}`], Styles[`attachment-${size}`], className)
                : classNames(
                    `border rounded d-flex align-items-center`,
                    (size === "sm" || size === "xs") ? "d-flex p-1" : "p-3 col-6 flex-grow-1",
                    `text-decoration-none  text-center`,
                    className,
                // eslint-disable-next-line react/jsx-indent
                )}
            href={url}
            target="_blank"
            rel="noreferrer"
        >

            <Icon className="fs-1 p-2 icon" />
            <span className={Styles["attachment-name"]}>
                {removeMiddleString(title, SIZE_TO_TEXT_LENGTH[size ?? "default"]) || "[Attachment]"}
            </span>
            {closeButton}
        </a>
    );
}
Attachment.defaultProps = {
    mimeType: "",
    title: "",
    size: "lg",
    className: "",
    isUploading: undefined,
    onClose: undefined,
    variant: "uploaded",
    controls: undefined,
    controlsClassName: undefined,
};
